import type { MutationKey, QueryKey } from '@tanstack/query-core';

import { GetValidationBillingQuotePayload } from '@/lib/api/client';
import type {
  ListBlockedIssuanceAuditParams,
  ListFrequentOffendersParams,
} from '@/lib/api/schemas/violations';

export const queryKeys = {
  locations: {
    list: (allowedLocationIds: string[] | 'all') =>
      ['locations', allowedLocationIds] satisfies QueryKey,
  },
  parkpliantGroups: {
    list: () => ['parkpliant-groups', 'list'] satisfies QueryKey,
  },
  sublocations: {
    list: (
      allowedLocationIds?: string[] | 'all',
      includeOtherLocationTypes?: boolean
    ) =>
      [
        'sublocations',
        { allowedLocationIds, includeOtherLocationTypes },
      ] satisfies QueryKey,
    quote: (input: GetValidationBillingQuotePayload) => [
      'validations',
      'billing',
      'quote',
      input,
    ],
    /** Partial key prefix used to invalidate every billing-quote query (any input)
     * after a sublocation save so the "Estimated cost" card recomputes against
     * the freshly persisted MinimumMonthlyFee / billing_type / max_validation_mins
     * without forcing the operator to hard-refresh. */
    quotePrefix: () => ['validations', 'billing', 'quote'] as const,
  },
  validations: {
    all: ['validations'] as const,
    detail: (id: string | undefined) =>
      [...queryKeys.validations.all, 'detail', id] as const,
    activeByPlate: (
      locationId: string | undefined,
      plate: string | undefined
    ) => [...queryKeys.validations.all, 'active', locationId, plate] as const,
    rotationSchedule: (locationId: string, subLocationId: string) =>
      [
        ...queryKeys.validations.all,
        'rotation',
        locationId,
        subLocationId,
      ] as const,
  },
  auth: {
    getIdTokenClaims: (isAuthenticated: boolean) => [
      'idTokenClaims',
      isAuthenticated,
    ],
  },
  violations: {
    blockedIssuanceAuditList: (
      locationId: string,
      params?: ListBlockedIssuanceAuditParams
    ) => ['violations', 'blocked-issuance-audit', locationId, params] as const,
    /** Partial key for setQueriesData to invalidate/update any blocked-issuance-audit query for a location. */
    blockedIssuanceAuditListPrefix: (locationId: string) =>
      ['violations', 'blocked-issuance-audit', locationId] as const,
    /** Distinct prefix from blocked-issuance-audit so broad invalidation does not affect this list. */
    frequentOffendersList: (
      locationId: string,
      params?: ListFrequentOffendersParams
    ) => ['violations', 'frequent-offenders', locationId, params] as const,
    /** Partial key to invalidate all frequent-offenders queries for a location. */
    frequentOffendersListPrefix: (locationId: string) =>
      ['violations', 'frequent-offenders', locationId] as const,
    /** Disposition reason catalog (void/waive picker source of truth). */
    dispositionReasonCatalog: () =>
      ['violations', 'disposition-reason-catalog'] as const,
  },
  health: {
    config: (locationId: string) => ['health', 'config', locationId] as const,
    diagnosesPrefix: (locationId: string) =>
      ['health', 'diagnoses', locationId] as const,
    diagnoses: (locationId: string, params?: object) =>
      ['health', 'diagnoses', locationId, params] as const,
    diagnosis: (locationId: string, diagnosisId: string) =>
      ['health', 'diagnosis', locationId, diagnosisId] as const,
  },
} as const;

export const mutationKeys = {
  locations: {
    update: () => ['location', 'update'] satisfies MutationKey,
  },
};
